import { MenuItem, useToast } from 'components';
import moment from 'moment-timezone';
import { useReinviteMemberToOrganization, useRemoveInvitation } from 'queries';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { InvitedOrganizationMember } from 'store';
import { Colors, Theme } from '@app/theme';
import { MemberItem } from './MemberItem';

const useStyles = createUseStyles((theme: Theme) => ({
  exp: {
    ...theme.fonts.caption,
    color: theme.colors.redAccessible,
    fontWeight: 'bold',
    marginLeft: theme.marginBase,
  },
  removeButton: {
    color: theme.colors.redAccessible,
  },
}));

interface Props {
  invitedMember: InvitedOrganizationMember;
}

export const InvitedTeamMemberItem = ({ invitedMember }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const isExpired = moment().isAfter(invitedMember.validUntil);

  const { mutateAsync } = useReinviteMemberToOrganization();
  const { mutateAsync: removeInvitation } = useRemoveInvitation(
    invitedMember.id,
  );
  const toast = useToast();

  const reInviteMember = useCallback(async () => {
    try {
      await mutateAsync({ email: invitedMember.email });
      toast.success(t(`teamInformations.invited`));
    } catch (e) {
      toast.exception(e);
    }
  }, [invitedMember.email, mutateAsync, toast, t]);

  const remove = useCallback(async () => {
    try {
      await removeInvitation();
      toast.success(t(`teamInformations.invitationRemoved`));
    } catch (e) {
      toast.exception(e);
    }
  }, [removeInvitation, toast, t]);

  const chip = useMemo(
    () => ({
      color: 'ink' as Colors,
      text: t(`teamInformations.roles.invited`),
    }),
    [t],
  );

  return (
    <MemberItem
      email={invitedMember.email}
      chip={chip}
      expired={isExpired}
      expDate={invitedMember.validUntil}
    >
      <MenuItem
        label={t('teamInformations.inviteAgain')}
        onClick={reInviteMember}
      />
      <MenuItem onClick={remove}>
        <div className={classes.removeButton}>
          {t('teamInformations.remove')}
        </div>
      </MenuItem>
    </MemberItem>
  );
};
